import React, { memo, useEffect, useMemo } from 'react';

import { LoadingIndicator } from 'features/store/pages/pickup-store-selector/components';
import { FlatList, StyleSheet } from 'react-native';

import Dialog from 'components/dialog';
import { Box, Typography } from 'components/ui';
import { AnchorLinksWidget } from 'pages/static/anchor-links-widget';
import { ImageWidget } from 'pages/static/image-widget';
import serializers from 'pages/static/render-current-static-page/serializers';
import {
  AnchorWidget,
  blockContentOverrides,
} from 'pages/static/render-current-static-page/static-page-container';
import {
  BlockWidgetContainer,
  blockWidgetLinkProps,
  blockWidgetStrongProps,
} from 'pages/static/render-current-static-page/styled';
import { WidgetType } from 'pages/static/render-current-static-page/widgetEnums';
import { IStaticPageRoute } from 'remote/queries/static-page';
import { useLocale } from 'state/intl';
import { PROD_DEFAULT_LANGUAGE, PROD_SUPPORTED_LANGUAGES } from 'state/intl/constants';
import { t } from 'state/intl/translations';
import { usePageManagerContext } from 'state/static-page-manager';
import { useUIContext } from 'state/ui';
import { spacing } from 'styles/constants';
import {
  AnchorContextProvider,
  AnchorScrollContainerRefType,
  useAnchorContext,
} from 'utils/static-page/hooks/useAnchorContext';

/**
 * This component is a simplified version of a static page presented in a dialog.
 * It has been streamlined by removing some elements to make it simpler.
 * For the time being, it will be used exclusively in the authentication flow for the links on the signup form.
 * However, if we wish to use this component in other contexts and with different pages in the future,
 * we would need to add back those removed elements.
 *
 */
export const DialogStaticPage = memo(() => {
  const { language: currentLanguage, region } = useLocale();
  const { dialogStaticPageState, setDialogStaticPageState } = useUIContext();

  const {
    currentPage,
    retrieveStaticPageById,
    routes: pageManagerRoutes,
    routesLoading,
    loadRoutes,
    loadRoutesHasBeenCalled,
    currentPageLoading,
  } = usePageManagerContext();

  useEffect(() => {
    if (dialogStaticPageState.isOpen && !loadRoutesHasBeenCalled) {
      loadRoutes();
    }
  }, [dialogStaticPageState.isOpen, loadRoutes, loadRoutesHasBeenCalled]);

  const targetPage = useMemo(() => {
    let result: undefined | { route: IStaticPageRoute; language: string };
    if (!loadRoutesHasBeenCalled || routesLoading) {
      return;
    }

    const staticPath = (dialogStaticPageState.page || '').replace(/^\/|\/$/g, '').toLowerCase();

    pageManagerRoutes.forEach(route => {
      const staticLocalizedPageLanguage = Object.keys(PROD_SUPPORTED_LANGUAGES).find(lang => {
        const localePath = route?.localePath;
        return localePath && lang in localePath && localePath[lang]?.current === staticPath;
      });

      if (staticLocalizedPageLanguage) {
        result = { route, language: staticLocalizedPageLanguage };
      }

      if (route.path.current?.toLowerCase() === staticPath) {
        result = { route, language: PROD_DEFAULT_LANGUAGE[region] };
      }
    });

    return result;
  }, [
    pageManagerRoutes,
    loadRoutesHasBeenCalled,
    routesLoading,
    dialogStaticPageState.page,
    region,
  ]);

  useEffect(() => {
    if (!targetPage) {
      return;
    }

    retrieveStaticPageById(targetPage.route._id);
  }, [targetPage, currentLanguage, retrieveStaticPageById, region]);

  const BodyComponent = () => {
    const { registerContainerRef } = useAnchorContext();
    const isTargetPageFetched = targetPage && targetPage.route._id === currentPage?._id;
    const isLoading =
      !loadRoutesHasBeenCalled ||
      routesLoading ||
      currentPageLoading ||
      (targetPage && !isTargetPageFetched);

    if (isLoading) {
      return <LoadingIndicator />;
    }

    if (!targetPage || !currentPage) {
      return (
        <Box style={styles.margin}>
          <Typography textAlign="center">{t('looksLikeWereExperiencingIssues')}</Typography>
        </Box>
      );
    }

    const currentWidgets = currentPage.widgets ? [...currentPage.widgets] : [];
    const listData = [...currentWidgets];

    const renderItem = ({ item: widget }: { item: any }) => {
      const type = widget._type;
      const key = widget._key;

      switch (type) {
        case WidgetType.ANCHOR:
          return (
            <Box key={key}>
              {/* @ts-expect-error TS(2322) FIXME: Type '{ className: string; id: any; }' is not assi... Remove this comment to see the full error message */}
              <AnchorWidget className="anchorWidget" id={widget.anchorName?.locale} />
            </Box>
          );
        case WidgetType.ANCHOR_LINKS:
          return (
            <Box key={key}>
              <AnchorLinksWidget widgets={currentPage.widgets} />
            </Box>
          );
        case WidgetType.IMAGE:
          return (
            <Box key={key}>
              <ImageWidget {...widget} />
            </Box>
          );
        case WidgetType.LOCALE_BLOCK_TEXT:
          return (
            <Box key={key}>
              <BlockWidgetContainer
                blockContentOverrides={blockContentOverrides(currentPage.path.current)}
                strongProps={blockWidgetStrongProps}
                linkProps={blockWidgetLinkProps}
                serializers={serializers}
                content={widget?.localeBlockTextContent?.localeRaw}
              />
            </Box>
          );
        default:
          return null;
      }
    };

    return (
      <FlatList
        data={listData}
        keyExtractor={item => item._key}
        ref={ref => {
          return registerContainerRef(ref as AnchorScrollContainerRefType);
        }}
        renderItem={renderItem}
      />
    );
  };

  const handleOnDismiss = () =>
    setDialogStaticPageState({ ...dialogStaticPageState, isOpen: false });

  return (
    <AnchorContextProvider>
      <Dialog
        showDialog={dialogStaticPageState.isOpen}
        modalAppearanceEventMessage={currentPage?.title}
        headingComponent={
          <Typography variant="headerTwo" style={styles.heading}>
            {currentPage?.title}
          </Typography>
        }
        bodyComponent={<BodyComponent />}
        accessibilityLabel={currentPage?.title}
        showCloseButton
        closeButtonLabelId="okay"
        onDismiss={handleOnDismiss}
      />
    </AnchorContextProvider>
  );
});

const styles = StyleSheet.create({
  heading: {
    textAlign: 'center',
  },
  margin: {
    margin: spacing.$4,
  },
});
